import React from 'react';
import { Box, Card, Grid, Typography, Tooltip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  LocalHospital, // For hospitals
  EmergencyShare, // For emergency calls
  TimelapseRounded, // For response time
  AmbulanceIcon, // You'll need to import this from a medical icon pack
  MedicalServices,
  Speed,
  LocationOn,
  PeopleAlt,
  Timer,
  Healing,
} from '@mui/icons-material';
import CountUp from 'react-countup';
import PropTypes from 'prop-types';

// Styled Components
const StatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  borderRadius: 24,
}));

const EmergencyCard = styled(Card)(({ gradient }) => ({
  position: 'relative',
  padding: '5px',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: gradient,
  borderRadius: 20,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .emergency-icon': {
      transform: 'scale(1.1)',
    },
  },
}));

const EmergencyIconContainer = styled(Box)({
  position: 'absolute',
  right: '-15px',
  top: '-15px',
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.2,
  transition: 'transform 0.3s ease-in-out',
});

const StatValue = styled(Typography)({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  zIndex: 1,
});

const EmergencyLabel = styled(Typography)({
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.9)',
  fontWeight: 500,
  zIndex: 1,
});

const ResponseTimeIndicator = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: 'rgba(255,255,255,0.9)',
  fontSize: '0.875rem',
  marginTop: '8px',
});

export const AmbulanceStatistics = ({ dashboard }) => {
  const emergencyStats = [
    {
      title: 'Active Ambulances',
      value: dashboard.activeAmbulances,
      icon: MedicalServices,
      gradient: 'linear-gradient(135deg, #FF3366 0%, #FF0033 100%)',
      subtitle: 'Currently on duty',
      status: `${dashboard.availableAmbulances} available`,
    },
    {
      title: 'Emergency Calls',
      value: dashboard.emergencyCalls,
      icon: EmergencyShare,
      gradient: 'linear-gradient(135deg, #4834D4 0%, #686DE0 100%)',
      subtitle: 'Last 24 hours',
      status: `${dashboard.pendingCalls} pending`,
    },
    {
      title: 'Non-Emergency Calls',
      value: dashboard.nonEmergencyCalls,
      icon: Timer,
      gradient: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
      subtitle: 'Minutes per call',
      status: 'Target: 8 mins',
    },
    {
      title: 'Critical Cases',
      value: dashboard.criticalCases,
      icon: Healing,
      gradient: 'linear-gradient(135deg, #E056FD 0%, #BE2EDD 100%)',
      subtitle: 'High priority cases',
      status: `${dashboard.successRate}% success rate`,
    },
  ];

  // Additional Statistics
  const additionalStats = [
    {
      title: 'Moderate Severity',
      value: dashboard.moderateCases,
      icon: Healing,
      gradient: 'linear-gradient(135deg, #FA8231 0%, #FC5C65 100%)',
      subtitle: 'moderate cases',
    },
    {
      title: 'Hospital Network',
      value: dashboard.connectedHospitals,
      icon: LocalHospital,
      gradient: 'linear-gradient(135deg, #20BF6B 0%, #0FB855 100%)',
      subtitle: 'Connected facilities',
    },
    {
      title: 'Coverage Area',
      value: dashboard.coverageArea,
      icon: LocationOn,
      gradient: 'linear-gradient(135deg, #45AAF2 0%, #2D98DA 100%)',
      subtitle: 'Square kilometers',
    },
  ];

  const formatSpecialityData = (specialityCategory) => {
    if (!specialityCategory || typeof specialityCategory !== "object") return [];
  
    return Object.entries(specialityCategory).map(([key, value]) => ({
      label: key, 
      value: value, 
      color: generateColor(value), 
    }));
  };
  
  const generateColor = (value) => {
    const colors = ["#FF3366", "#4834D4", "#2ECC71", "#F97F51", "#45AAF2"];
    return colors[value % colors.length]; 
  };
  
  const specialityData = formatSpecialityData(dashboard?.specialityCategory || {});


  return (
    <StatsContainer>
      <Box mb={4}>
        <Typography variant="h5" fontWeight={600} color="error.main">
          Emergency Statistics
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
         
        </Typography>
      </Box>

      {/* Main Emergency Stats */}
      <Grid container spacing={3} mb={4}>
        {emergencyStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <EmergencyCard gradient={stat.gradient}>
              <EmergencyIconContainer className="emergency-icon">
                <stat.icon sx={{ fontSize: 60, color: 'white' }} />
              </EmergencyIconContainer>

              <Box>
                <EmergencyLabel>{stat.title}</EmergencyLabel>
                <StatValue>
                  <CountUp 
                    end={stat.value} 
                    duration={2} 
                    separator="," 
                    suffix={stat.title.includes('Time') ? 'm' : ''}
                  />
                </StatValue>
                <ResponseTimeIndicator>
                  <Timer sx={{ fontSize: 16 }} />
                  {stat.status}
                </ResponseTimeIndicator>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {stat.subtitle}
                </Typography>
              </Box>
            </EmergencyCard>
          </Grid>
        ))}
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3}>
        {additionalStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <EmergencyCard gradient={stat.gradient}>
              <EmergencyIconContainer className="emergency-icon">
                <stat.icon sx={{ fontSize: 50, color: 'white' }} />
              </EmergencyIconContainer>

              <Box>
                <EmergencyLabel>{stat.title}</EmergencyLabel>
                <StatValue>
                  <CountUp 
                    end={stat.value} 
                    duration={2} 
                    separator="," 
                    suffix={stat.title.includes('Rate') ? '%' : 
                        stat.title.includes('Area') ? ' km²' : ''}
               />
             </StatValue>
             <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
               {stat.subtitle}
             </Typography>
           </Box>
         </EmergencyCard>
       </Grid>
     ))}
   </Grid>

   {/* Emergency Response Status Section */}
   <Box mt={4}>
     <Typography variant="h5" fontWeight={600} color="error.main">
      Live Emergency Response Status
     </Typography>
    
     <Grid container spacing={3}>

       {/* <Grid item xs={12} md={6}>
         <Card sx={{ p: 3, borderRadius: 4 }}>
           <Box display="flex" justifyContent="space-between" alignItems="center">
             <Typography variant="h6" color="error.main">
               Current Response Times
             </Typography>
             <Tooltip title="Average response time in different zones">
               <Timer color="error" />
             </Tooltip>
           </Box>
           <Box display="flex" gap={3} mt={2}>
             <Box>
               <Typography variant="subtitle2" color="text.secondary">
                 Urban Areas
               </Typography>
               <Typography variant="h4" fontWeight="bold">
                 {dashboard.urbanResponseTime}m
               </Typography>
             </Box>
             <Box>
               <Typography variant="subtitle2" color="text.secondary">
                 Suburban
               </Typography>
               <Typography variant="h4" fontWeight="bold">
                 {dashboard.suburbanResponseTime}m
               </Typography>
             </Box>
             <Box>
               <Typography variant="subtitle2" color="text.secondary">
                 Rural
               </Typography>
               <Typography variant="h4" fontWeight="bold">
                 {dashboard.ruralResponseTime}m
               </Typography>
             </Box>
           </Box>
         </Card>
       </Grid> */}


       {/* <Grid item xs={12} md={6}>
         <Card sx={{ p: 3, borderRadius: 4 }}>
           <Box display="flex" justifyContent="space-between" alignItems="center">
             <Typography variant="h6" color="primary.main">
               Resource Availability
             </Typography>
             <Tooltip title="Current resource status">
               <MedicalServices color="primary" />
             </Tooltip>
           </Box>
           <Grid container spacing={2} mt={1}>
             <Grid item xs={4}>
               <Box textAlign="center">
                 <CircularProgress 
                   variant="determinate" 
                   value={(dashboard.availableAmbulances / dashboard.totalAmbulances) * 100}
                   color="success"
                   size={80}
                 />
                 <Typography variant="subtitle2" mt={1}>
                   Ambulances
                 </Typography>
               </Box>
             </Grid>
             <Grid item xs={4}>
               <Box textAlign="center">
                 <CircularProgress 
                   variant="determinate" 
                   value={(dashboard.availableStaff / dashboard.totalStaff) * 100}
                   color="info"
                   size={80}
                 />
                 <Typography variant="subtitle2" mt={1}>
                   Medical Staff
                 </Typography>
               </Box>
             </Grid>
             <Grid item xs={4}>
               <Box textAlign="center">
                 <CircularProgress 
                   variant="determinate" 
                   value={(dashboard.availableEquipment / dashboard.totalEquipment) * 100}
                   color="warning"
                   size={80}
                 />
                 <Typography variant="subtitle2" mt={1}>
                   Equipment
                 </Typography>
               </Box>
             </Grid>
           </Grid>
         </Card>
       </Grid> */}

       <Grid item xs={12}>
         <Card sx={{ p: 3, borderRadius: 4 }}>
           {/* <Typography variant="h6" mb={2}>
             Emergency Categories Distribution
           </Typography> */}
           <Grid container spacing={2}>
             {specialityData?.map((category) => (
               <Grid item xs={12} sm={6} md={2.4} key={category.label}>
                 <Box
                   sx={{
                     p: 2,
                     borderRadius: 2,
                     bgcolor: `${category.color}15`,
                     border: `1px solid ${category.color}30`,
                   }}
                 >
                   <Typography variant="subtitle2" color="text.secondary">
                     {category.label}
                   </Typography>
                   <Typography variant="h4" color={category.color} fontWeight="bold">
                     {category.value}
                   </Typography>
                 </Box>
               </Grid>
             ))}
           </Grid>
         </Card>
       </Grid>
     </Grid>
   </Box>
 </StatsContainer>
);
};

// PropTypes
AmbulanceStatistics.propTypes = {
dashboard: PropTypes.shape({
 activeAmbulances: PropTypes.number.isRequired,
 availableAmbulances: PropTypes.number.isRequired,
 emergencyCalls: PropTypes.number.isRequired,
 pendingCalls: PropTypes.number.isRequired,
 avgResponseTime: PropTypes.number.isRequired,
 criticalCases: PropTypes.number.isRequired,
 successRate: PropTypes.number.isRequired,
 connectedHospitals: PropTypes.number.isRequired,
 medicalStaff: PropTypes.number.isRequired,
 coverageArea: PropTypes.number.isRequired,
 responseRate: PropTypes.number.isRequired,
 urbanResponseTime: PropTypes.number.isRequired,
 suburbanResponseTime: PropTypes.number.isRequired,
 ruralResponseTime: PropTypes.number.isRequired,
 // Resource availability
 totalAmbulances: PropTypes.number.isRequired,
 totalStaff: PropTypes.number.isRequired,
 totalEquipment: PropTypes.number.isRequired,
 availableStaff: PropTypes.number.isRequired,
 availableEquipment: PropTypes.number.isRequired,
 // Emergency categories
 cardiacCases: PropTypes.number.isRequired,
 traumaCases: PropTypes.number.isRequired,
 medicalCases: PropTypes.number.isRequired,
 trafficCases: PropTypes.number.isRequired,
 otherCases: PropTypes.number.isRequired,
}).isRequired,
};

export default AmbulanceStatistics;